import { useState } from 'react';
import { CalendarClock, Pencil } from 'lucide-react';
import { TENSION_LEVELS, fmtDateFr, isOverdue } from './tensionMeta';
import { TensionPill } from './TensionPill';

const ORDER = { critique: 0, tension: 1, vigilance: 2 };

export const TensionTab = ({ tensions, cornersById, canEdit, onEdit }) => {
  const [showLifted, setShowLifted] = useState(false);
  const [levelFilter, setLevelFilter] = useState(null);
  const rows = tensions.filter(t => (showLifted || t.active) && (!levelFilter || (t.active && t.level === levelFilter)))
    .sort((a, b) => (a.active === b.active ? (ORDER[a.level] ?? 9) - (ORDER[b.level] ?? 9) || (a.relance_date || '').localeCompare(b.relance_date || '') : a.active ? -1 : 1));
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900" data-testid="tension-tab">
      <div className="px-4 py-3 flex items-center gap-4 border-b border-slate-100 dark:border-slate-800 text-sm">
        <button type="button" onClick={() => setLevelFilter(null)} data-testid="tension-filter-all"
          className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${!levelFilter ? 'bg-slate-800 text-white border-slate-800' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
          Tous ({tensions.filter(t => t.active).length})
        </button>
        {Object.entries(TENSION_LEVELS).map(([k, m]) => (
          <button key={k} type="button" onClick={() => setLevelFilter(levelFilter === k ? null : k)} data-testid={`tension-count-${k}`}
            className={`flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${levelFilter === k ? m.pill + ' ring-2 ring-offset-1 ring-current' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
            <span className={`w-2.5 h-2.5 rounded-full ${m.dot}`} />{m.label} : <strong>{tensions.filter(t => t.active && t.level === k).length}</strong>
          </button>
        ))}
        <label className="ml-auto flex items-center gap-2 text-xs text-slate-500">
          <input type="checkbox" checked={showLifted} onChange={e => setShowLifted(e.target.checked)} data-testid="tension-show-lifted" /> Afficher les tensions levées
        </label>
      </div>
      <table className="w-full text-sm">
        <thead className="text-xs uppercase text-slate-400">
          <tr>{['Corner', 'Niveau', 'Motif', 'Commentaire', 'En tension depuis', 'Relance', 'Dernière mise à jour', ''].map(h => <th key={h} className="text-left px-4 py-2 font-medium">{h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map(t => (
            <tr key={t.corner_id} className={`border-t border-slate-100 dark:border-slate-800 ${t.active ? TENSION_LEVELS[t.level]?.row : 'opacity-60'}`} data-testid={`tension-row-${t.corner_id}`}>
              <td className="px-4 py-2"><span className="font-semibold">{t.corner_name}</span><span className="block text-xs text-slate-400">{t.name_sf}</span></td>
              <td className="px-4 py-2">{t.active ? <TensionPill level={t.level} /> : <span className="text-xs text-emerald-700 font-semibold">Levée</span>}</td>
              <td className="px-4 py-2">{t.motif || '—'}</td>
              <td className="px-4 py-2 max-w-xs text-slate-600 truncate" title={t.comment}>{t.comment || '—'}</td>
              <td className="px-4 py-2 whitespace-nowrap">{fmtDateFr(t.since)}</td>
              <td className={`px-4 py-2 whitespace-nowrap ${t.active && isOverdue(t.relance_date) ? 'text-red-600 font-semibold' : ''}`}>
                {t.active && t.relance_date ? <span className="inline-flex items-center gap-1"><CalendarClock className="w-3.5 h-3.5" />{fmtDateFr(t.relance_date)}</span> : '—'}
              </td>
              <td className="px-4 py-2 text-xs text-slate-500 whitespace-nowrap">{fmtDateFr(t.updated_at)} · {t.updated_by}</td>
              <td className="px-4 py-2 text-right">
                <button type="button" onClick={() => onEdit(cornersById[t.corner_id] || { id: t.corner_id, name_powerbi: t.corner_name })}
                  className="inline-flex items-center gap-1 text-xs font-semibold text-orange-700 hover:underline" data-testid={`tension-edit-${t.corner_id}`}>
                  <Pencil className="w-3.5 h-3.5" />{canEdit ? 'Modifier / historique' : 'Historique'}
                </button>
              </td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-400">Aucun corner en tension. Utilisez le drapeau d'une ligne du tableau détaillé pour en ajouter.</td></tr>}
        </tbody>
      </table>
    </div>
  );
};
